
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import SEO from '../components/SEO';
import { useLocation, useNavigate } from 'react-router-dom';
import { projects } from '../data/projects';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Globe, ArrowLeft } from 'lucide-react';
import { useSound } from '../hooks/useSound';

// ---- DEDICATED EDITORIAL LAYOUT FOR LOVEMATCH ----
const LoveMatchDetails = ({ project, onBack }: { project: any, onBack: () => void }) => {
    const { playClick } = useSound();
    const [showMore, setShowMore] = useState(false);

    const { scrollY } = useScroll();
    const rawTop = useTransform(
      scrollY,
      [0, 400],
      [window.innerWidth < 768 ? 112 : 128, window.innerWidth < 768 ? 12 : 42]
    );
    const topPosition = useSpring(rawTop, { stiffness: 100, damping: 30, restDelta: 0.001 });

    // Using the exact images provided from the webflow case study layout requested
    const heroImage = "https://cdn.prod.website-files.com/66a9115885e96a3e9e2d5d52/69bc6906c5de038133cfb33d_1.webp";
    
    const editorialImages = [
        "https://cdn.prod.website-files.com/66a9115885e96a3e9e2d5d52/69bfc1f3c318c76bac5d6d30_2.webp",
        "https://res.cloudinary.com/dwxijtvom/image/upload/v1774518350/txyoom3dikxgu4zmrfxt.jpg",
        "https://cdn.prod.website-files.com/66a9115885e96a3e9e2d5d52/69bfce8e805e504d421e3ce8_4.png",
        "https://cdn.prod.website-files.com/66a9115885e96a3e9e2d5d52/69bfcfa2c4be786beb8dd456_5.webp",
        "https://cdn.prod.website-files.com/66a9115885e96a3e9e2d5d52/69bfc2d841c0de3b5f6a18c0_6.png",
        "https://cdn.prod.website-files.com/66a9115885e96a3e9e2d5d52/69bc6c23526f74398ce6e71a_7.webp",
        "https://cdn.prod.website-files.com/66a9115885e96a3e9e2d5d52/69bc6d4b6539a3be9a7f0b0a_11.webp"
    ];

    return (
        <div className="min-h-screen bg-[#FDFCF8] text-[#1A1A1A] font-sans selection:bg-black/10">
            <SEO
                title={`${project.title} | TDMA`}
                description={project.shortDescription}
                image={heroImage}
                url={`https://tdma.one${project.slug}`}
            />

            {/* Sticky Navigation / Back Button - Portal to body to avoid ScrollSmoother translation */}
            {typeof document !== 'undefined' && ReactDOM.createPortal(
                <motion.div 
                    style={{ top: topPosition }}
                    className="fixed left-6 md:left-10 z-[60]"
                >
                    <button 
                        onClick={() => { playClick(); onBack(); }}
                        className="group flex items-center justify-center gap-3 bg-white border border-black/10 backdrop-blur-md px-4 py-2 md:px-6 md:py-2.5 rounded-lg shadow-md hover:bg-black hover:text-white transition-all duration-300"
                    >
                        <ArrowLeft className="w-4 h-4 text-black group-hover:text-white transition-colors" />
                        <span className="hidden md:block text-black group-hover:text-black font-medium text-xs md:text-sm transition-colors">Back</span>
                    </button>
                </motion.div>,
                document.body
            )}

            {/* FULL WIDTH HERO */}
            <div className="w-full h-[70vh] md:h-screen relative overflow-hidden">
                <img src={heroImage} alt="Cover" className="w-full h-full object-cover" />
            </div>

            {/* EDITORIAL CONTENT & INFO SECTION */}
            <section className="px-6 sm:px-12 md:px-20 py-20 md:py-32 w-full max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-24">
                {/* Left Title Space */}
                <div className="md:col-span-4 flex items-start">
                     <h1 className="text-6xl md:text-7xl lg:text-[7rem] tracking-tighter leading-[0.9] font-medium uppercase">
                         LOVE<br/>MATCH
                     </h1>
                </div>

                {/* Right Content Space */}
                <div className="md:col-span-8 flex flex-col gap-8 md:gap-12">
                    <p className="text-2xl md:text-3xl lg:text-[2rem] text-black/80 font-light leading-[1.3] max-w-4xl tracking-tight">
                        {project.fullDescription}
                    </p>
                    
                    {/* Collapsible Meta Data List Table */}
                    <div className="w-full max-w-3xl mt-4 md:mt-8">
                        <button 
                            onClick={() => setShowMore(!showMore)} 
                            className="flex items-center gap-4 text-black font-medium tracking-widest text-xs md:text-sm border-b border-transparent hover:border-black/30 transition-all uppercase pb-1 mb-8"
                        >
                            <span>{showMore ? '-' : '+'}</span> 
                            {showMore ? 'less information' : 'more information'}
                        </button>
                        
                        <motion.div 
                            initial={false}
                            animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
                            className="overflow-hidden"
                        >
                            <ul className="flex flex-col w-full text-black/90 tracking-wide">
                                <li className="grid grid-cols-1 sm:grid-cols-2 py-6 border-t border-black/10">
                                    <span className="text-black/50 text-xs sm:text-sm tracking-[0.2em] font-medium uppercase mb-2 sm:mb-0">PROJECT TYPE</span>
                                    <span className="text-lg font-light leading-snug">{project.category.text} Development</span>
                                </li>
                                <li className="grid grid-cols-1 sm:grid-cols-2 py-6 border-t border-black/10">
                                    <span className="text-black/50 text-xs sm:text-sm tracking-[0.2em] font-medium uppercase mb-2 sm:mb-0">CLIENT NAME</span>
                                    <span className="text-lg font-light leading-snug">{project.title} Global</span>
                                </li>
                                <li className="grid grid-cols-1 sm:grid-cols-2 py-6 border-t border-black/10">
                                    <span className="text-black/50 text-xs sm:text-sm tracking-[0.2em] font-medium uppercase mb-2 sm:mb-0">TECH STACK</span>
                                    <span className="text-lg font-light leading-snug">{project.techStack.join(', ')}</span>
                                </li>
                                <li className="grid grid-cols-1 sm:grid-cols-2 py-6 border-t border-black/10">
                                    <span className="text-black/50 text-xs sm:text-sm tracking-[0.2em] font-medium uppercase mb-2 sm:mb-0">TIMELINE</span>
                                    <span className="text-lg font-light leading-snug">1-2 Months</span>
                                </li>
                                <li className="grid grid-cols-1 sm:grid-cols-2 py-6 border-t border-black/10">
                                    <span className="text-black/50 text-xs sm:text-sm tracking-[0.2em] font-medium uppercase mb-2 sm:mb-0">YEAR</span>
                                    <span className="text-lg font-light leading-snug">2025</span>
                                </li>
                                <li className="grid grid-cols-1 sm:grid-cols-2 py-6 border-t border-black/10 text-lg font-light">
                                    <span className="text-black/50 text-xs sm:text-sm tracking-[0.2em] font-medium uppercase mb-2 sm:mb-0 mt-1">AUTHORS</span>
                                    <div className="flex justify-between w-full pr-8">
                                        <div className="flex flex-col gap-2">
                                            <span>Fullstack Team</span>
                                            <span>UI/UX Team</span>
                                        </div>
                                        <div className="flex flex-col gap-2 text-black/40 text-[0.65rem] sm:text-xs tracking-[0.2em] font-medium uppercase mt-1 text-right">
                                            <span>DEVELOPMENT</span>
                                            <span>DESIGN</span>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FULL WIDTH EDITORIAL IMAGE SCROLL */}
            <div className="w-full flex flex-col items-center bg-[#FDFCF8] overflow-hidden">
                {editorialImages.map((img, i) => (
                    <img key={i} src={img} alt={`Gallery Editorial ${i}`} className="w-full object-cover block" />
                ))}
            </div>
            
        </div>
    );
};
// ---------------------------------------------------

const ProjectDetails: React.FC = () => {
    const { playClick } = useSound();
    const location = useLocation();
    const navigate = useNavigate();
    // Find project based on current path (e.g., /vgfoods)
    const project = projects.find(p => p.slug === location.pathname);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!project) {
            // Optional: redirect to home or show 404 if not found
            // navigate('/');
        }
    }, [project, navigate]);

    if (!project) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <p>Project not found</p>
            </div>
        );
    }

    if (project.id === 'lovematch') {
        return <LoveMatchDetails project={project} onBack={() => navigate('/')} />;
    }

    return (
        <div className="min-h-screen bg-black text-white font-sans selection:bg-white/20 pt-24 sm:pt-32">
            <SEO
                title={`${project.title} | TDMA`}
                description={project.shortDescription || project.fullDescription.substring(0, 150)}
                image={project.thumbnailSrc}
                url={`https://tdma.one${project.slug}`}
            />

            <div className="max-w-[1400px] mx-auto">
                {/* Header Section (Title, Desc, Tech) */}
                <section className="px-5 sm:px-12 md:px-16 mb-12 sm:mb-20">
                    <div className="flex flex-col gap-8 md:gap-12">
                        {/* Back Button */}
                        <motion.button
                            onClick={() => { playClick(); navigate('/'); }}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            className="w-fit flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm text-white/80 hover:text-white transition-all group"
                        >
                            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                            Back to Home
                        </motion.button>

                        {/* Title & Location */}
                        <div>
                            <div className="flex flex-wrap items-center gap-4">
                                <motion.h1
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    className="text-4xl xs:text-5xl sm:text-6xl md:text-8xl lg:text-[7rem] font-medium tracking-tighter leading-[0.9] break-words"
                                >
                                    {project.title}
                                </motion.h1>
                                {project.websiteLink && (
                                    <motion.a
                                        href={project.websiteLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={playClick}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                        className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-all group mt-2 sm:mt-4"
                                        title="Visit Live Site"
                                    >
                                        <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-white group-hover:scale-110 transition-transform" />
                                    </motion.a>
                                )}
                            </div>
                            <motion.span
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                                className="block text-xl sm:text-3xl md:text-4xl text-white/50 font-light mt-3 sm:mt-4"
                            >
                                {project.location}
                            </motion.span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-24 items-start">
                            {/* Description */}
                            <motion.div
                                className="lg:col-span-2"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                            >
                                <p className="text-lg sm:text-2xl md:text-3xl font-light text-white/90 leading-relaxed">
                                    {project.fullDescription}
                                </p>
                            </motion.div>

                            {/* Tech Stack */}
                            <motion.div
                                className="lg:col-span-1"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                            >
                                <h3 className="text-xs sm:text-sm uppercase tracking-widest text-white/40 mb-4 sm:mb-6 font-medium">Technologies</h3>
                                <div className="flex flex-wrap gap-2 sm:gap-3">
                                    {project.techStack.map((tech, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white/5 border border-white/10 rounded-full text-xs sm:text-sm font-light text-white/80"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Main Project Image */}
                <motion.section
                    className="px-4 sm:px-6 md:px-12"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                >
                    <div className={`relative w-full overflow-hidden rounded-xl sm:rounded-3xl shadow-2xl shadow-white/5 ${project.disableCrop ? '' : 'aspect-video md:aspect-[16/9] lg:aspect-[21/9]'}`}>
                        {project.mediaType === 'video' ? (
                            <video
                                src={project.thumbnailSrc} // Fallback if detailVideoSrc not separate
                                autoPlay muted loop playsInline
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <img
                                src={project.detailImageSrc}
                                alt={`${project.title} showcase`}
                                className={`w-full ${project.disableCrop ? 'h-auto' : 'h-full object-cover'}`}
                                style={project.mediaStyle}
                            />
                        )}
                    </div>
                </motion.section>

                {/* Challenge & Solution */}
                <section className="px-5 sm:px-12 md:px-16 py-16 sm:py-24 md:py-32">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24">
                        {/* Challenge */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-2xl sm:text-3xl md:text-4xl font-light mb-6 sm:mb-8 flex items-center">
                                <span className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full mr-3 sm:mr-4"></span>
                                The Challenge
                            </h2>
                            <p className="text-base sm:text-xl text-white/70 leading-relaxed font-light">
                                {project.challenge}
                            </p>
                        </motion.div>

                        {/* Solution */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h2 className="text-2xl sm:text-3xl md:text-4xl font-light mb-6 sm:mb-8 flex items-center">
                                <span className="w-2 h-2 sm:w-3 sm:h-3 bg-white/20 rounded-full mr-3 sm:mr-4"></span>
                                The Solution
                            </h2>
                            <p className="text-base sm:text-xl text-white/70 leading-relaxed font-light">
                                {project.solution}
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Project Gallery */}
                {project.galleryImages && project.galleryImages.length > 0 && (
                    <section className="px-5 sm:px-12 md:px-16 pb-16 sm:pb-24">
                        <h3 className="text-xs sm:text-sm uppercase tracking-widest text-white/40 mb-8 sm:mb-12 font-medium">Project Gallery</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
                            {project.galleryImages.map((img, idx) => {
                                // Logic to span full width if it's the last item and we have an odd number of images
                                const isLastItem = idx === project.galleryImages.length - 1;
                                const isOddTotal = project.galleryImages.length % 2 !== 0;
                                const spanClass = (isLastItem && isOddTotal) ? "md:col-span-2" : "";

                                return (
                                    <motion.div
                                        key={idx}
                                        className={`overflow-hidden rounded-xl sm:rounded-2xl shadow-lg border border-white/5 ${spanClass}`}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: idx * 0.1 }}
                                    >
                                        <img
                                            src={img}
                                            alt={`${project.title} gallery ${idx + 1}`}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </motion.div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Results */}
                <section className="px-5 sm:px-12 md:px-16 py-16 sm:py-24 md:py-32 bg-white text-black rounded-t-[2rem] sm:rounded-t-[5rem]">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl sm:text-6xl md:text-7xl font-light mb-8 sm:mb-10 tracking-tight">Results</h2>
                        <p className="text-lg sm:text-2xl md:text-3xl font-light leading-relaxed mb-10 sm:mb-12">
                            {project.results}
                        </p>
                        {project.websiteLink && (
                            <a
                                href={project.websiteLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 sm:gap-3 px-6 py-3 sm:px-8 sm:py-4 bg-black text-white rounded-full text-base sm:text-lg font-medium hover:bg-zinc-800 transition-all hover:gap-4 sm:hover:gap-5"
                            >
                                Visit Website
                                <span>→</span>
                            </a>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ProjectDetails;
